import {
  supabase,
  clearStaleAuth,
  SUPABASE_URL,
  isAuthRefreshError,
  handleAuthRefreshError,
} from '@/lib/supabase';
import { clearStalePortalProfileCache, fetchPortalProfile, normalizePortalEmail } from '@/lib/portalProfile';

export type LoginError = string;

export type UserType = 'management' | 'subcontractor' | 'client';

export interface LoginResult {
  success: true;
  userType: UserType;
}

export interface LoginFailure {
  success: false;
  error: string;
}

const AUTH_SIGN_IN_TIMEOUT_MS = 15_000;
const AUTH_PROFILE_TIMEOUT_MS = 10_000;

function withTimeout<T>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    Promise.resolve(promise),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms),
    ),
  ]);
}

/**
 * Prepare for a fresh sign-in without calling getSession().
 * getSession() here deadlocks with the subsequent signInWithPassword call.
 */
async function releaseAuthLockBeforeLogin(): Promise<void> {
  clearStaleAuth();
  clearStalePortalProfileCache();
}

export function getLoginRedirectPath(userType: UserType): string {
  if (userType === 'management') return '/management/dashboard';
  if (userType === 'subcontractor') return '/subcontractor/dashboard';
  return '/client/portal';
}

/**
 * Shared login function used by ALL role login pages.
 * Authenticates with Supabase Auth, then looks up role in public.jphph_profiles.
 * Never uses hardcoded test emails. Never uses fallback tables.
 */
export async function loginWithEmail(
  email: string,
  password: string,
): Promise<LoginResult | LoginFailure> {
  const startedAt = performance.now();
  const logTiming = (phase: string) => {
    console.log(`[login] ⏱ ${phase}: ${(performance.now() - startedAt).toFixed(0)}ms`);
  };

  clearStaleAuth();
  await releaseAuthLockBeforeLogin();
  logTiming('pre-login session release');

  console.log('Login email:', email);
  console.log('Supabase URL:', SUPABASE_URL);

  let signInData: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>['data'];
  let signInError: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>['error'];

  try {
    const signInResult = await withTimeout(
      supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      }),
      AUTH_SIGN_IN_TIMEOUT_MS,
      'signInWithPassword',
    );
    signInData = signInResult.data;
    signInError = signInResult.error;
  } catch (err) {
    logTiming('signInWithPassword (failed)');
    const msg = err instanceof Error ? err.message : String(err);
    if (isAuthRefreshError(err)) {
      await handleAuthRefreshError(err);
    }
    return {
      success: false,
      error: msg.includes('timed out')
        ? 'Sign-in timed out. Please try again.'
        : msg || 'Sign-in failed. Please try again.',
    };
  }

  logTiming('signInWithPassword');

  if (signInError || !signInData?.user) {
    console.log('[login] Auth failed:', signInError?.message);
    return {
      success: false,
      error: signInError?.message || 'Invalid email or password',
    };
  }

  const userId = signInData.user.id;
  const normalizedEmail = normalizePortalEmail(email) ?? normalizePortalEmail(signInData.user.email);
  console.log('Auth user id:', userId, 'email:', normalizedEmail);

  if (!signInData.session) {
    console.warn('[login] No session in signInWithPassword response');
  }

  let profile: Awaited<ReturnType<typeof fetchPortalProfile>>['profile'];
  let profileSource: string;
  let profileFetchError: string | undefined;

  try {
    const profileResult = await withTimeout(
      fetchPortalProfile(userId, normalizedEmail),
      AUTH_PROFILE_TIMEOUT_MS,
      'profile fetch',
    );
    profile = profileResult.profile;
    profileSource = profileResult.source;
    profileFetchError = profileResult.error;
  } catch (err) {
    logTiming('profile fetch (failed)');
    const msg = err instanceof Error ? err.message : String(err);
    return {
      success: false,
      error: msg.includes('timed out')
        ? 'Login succeeded, but profile loading timed out. Please try again.'
        : `Login succeeded, but profile error: ${msg}`,
    };
  }

  logTiming('profile fetch');
  console.log('[login] Portal profile result:', profile, 'source:', profileSource, 'error:', profileFetchError);

  if (profileFetchError) {
    return {
      success: false,
      error: `Login succeeded, but profile query error: ${profileFetchError}`,
    };
  }

  if (!profile) {
    console.log('[login] Portal profile not found for user_id:', userId, 'source:', profileSource);
    return {
      success: false,
      error: 'Login succeeded, but portal profile was not found. Please contact support.',
    };
  }

  if (!profile.is_active) {
    console.log('[login] Profile is inactive for user_id:', userId);
    return {
      success: false,
      error: 'Account is inactive. Please contact support.',
    };
  }

  const userType = (profile.user_type ?? '').toLowerCase() as UserType;

  if (!['management', 'subcontractor', 'client'].includes(userType)) {
    console.log('[login] Invalid user_type:', userType);
    return {
      success: false,
      error: 'Invalid account type. Please contact support.',
    };
  }

  logTiming('complete');
  console.log('[login] Login complete. userType:', userType);
  return { success: true, userType };
}
